import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://idsysbackend.onrender.com/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Intercepteur pour ajouter le token aux requêtes
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Auth
export const login = async (username, password) => {
  const response = await api.post('/auth/login', { username, password });
  return response.data;
};

export const getUserProfile = async () => {
  const response = await api.get('/auth/profile');
  return response.data;
};

export const updatePassword = async (currentPassword, newPassword) => {
  const response = await api.put('/auth/password', {
    currentPassword,
    newPassword,
  });
  return response.data;
};

// Users (admin only)
export const getAllUsers = async () => {
  const response = await api.get('/utilisateurs');
  const data = response.data;
  if (Array.isArray(data)) {
    return data;
  }
  if (data && Array.isArray(data.data)) {
    return data.data;
  }
  if (data && Array.isArray(data.utilisateurs)) {
    return data.utilisateurs;
  }
  if (data && Array.isArray(data.users)) {
    return data.users;
  }
  console.warn('Réponse API inattendue:', data);
  return [];
};

export const getUserById = async (id) => {
  const response = await api.get(`/utilisateurs/${id}`);
  const data = response.data;
  // Gérer différentes structures de réponse
  if (data && data.data) {
    return data.data;
  }
  if (data && data.utilisateur) {
    return data.utilisateur;
  }
  if (data && data.user) {
    return data.user;
  }
  return data;
};

export const createUser = async (userData) => {
  const response = await api.post('/utilisateurs', userData);
  return response.data;
};

export const updateUser = async (id, userData) => {
  const response = await api.patch(`/utilisateurs/${id}`, userData);
  return response.data;
};

export const deleteUser = async (id) => {
  const response = await api.delete(`/utilisateurs/${id}`);
  return response.data;
};

// Cellules et Bureaux
export const getAllCellules = async () => {
  const response = await api.get('/cellules');
  const data = response.data;
  // L'API retourne { success: true, data: [...] }
  if (data && data.success && Array.isArray(data.data)) {
    return data.data;
  }
  if (Array.isArray(data)) {
    return data;
  }
  if (data && Array.isArray(data.cellules)) {
    return data.cellules;
  }
  console.warn('Réponse API inattendue pour cellules:', data);
  return [];
};

export const getAllBureaux = async () => {
  const response = await api.get('/bureaux');
  const data = response.data;
  // L'API retourne { success: true, data: [...] }
  if (data && data.success && Array.isArray(data.data)) {
    return data.data;
  }
  if (Array.isArray(data)) {
    return data;
  }
  if (data && Array.isArray(data.bureaux)) {
    return data.bureaux;
  }
  console.warn('Réponse API inattendue pour bureaux:', data);
  return [];
};

export const createBureau = async (bureauData) => {
  const response = await api.post('/bureaux', bureauData);
  return response.data;
};

export const createCellule = async (celluleData) => {
  const response = await api.post('/cellules', celluleData);
  return response.data;
};

// Agents
export const addAgent = async (agentData) => {
  // Si c'est un FormData, ne pas définir Content-Type (le navigateur le fera automatiquement avec le boundary)
  const config = agentData instanceof FormData 
    ? { headers: { 'Content-Type': undefined } }
    : {};
  const response = await api.post('/agents', agentData, config);
  return response.data;
};

export const getAllAgents = async () => {
  const response = await api.get('/agents');
  // Gérer différentes structures de réponse
  const data = response.data;
  if (Array.isArray(data)) {
    return data;
  }
  if (data && Array.isArray(data.data)) {
    return data.data;
  }
  if (data && Array.isArray(data.agents)) {
    return data.agents;
  }
  // Si ce n'est pas un tableau, retourner un tableau vide
  console.warn('Réponse API inattendue:', data);
  return [];
};

export const getAgentById = async (id) => {
  const response = await api.get(`/agents/${id}`);
  return response.data;
};

export const updateAgent = async (id, agentData) => {
  // Si c'est un FormData, ne pas définir Content-Type (le navigateur le fera automatiquement avec le boundary)
  const config = agentData instanceof FormData 
    ? { headers: { 'Content-Type': undefined } }
    : {};
  const response = await api.patch(`/agents/${id}`, agentData, config);
  return response.data;
};

export const deleteAgent = async (id) => {
  const response = await api.delete(`/agents/${id}`);
  return response.data;
};

// Recherche côté client (filtrage des agents)
export const searchAgents = async (searchTerm) => {
  const allAgents = await getAllAgents();
  // S'assurer que allAgents est un tableau
  const agentsArray = Array.isArray(allAgents) ? allAgents : [];
  
  if (!searchTerm || !searchTerm.trim()) {
    return agentsArray;
  }
  
  const term = searchTerm.toLowerCase().trim();
  return agentsArray.filter((agent) => {
    return (
      agent.nom?.toLowerCase().includes(term) ||
      agent.prenom?.toLowerCase().includes(term) ||
      agent.matricule?.toLowerCase().includes(term) ||
      agent.bureau?.toLowerCase().includes(term) ||
      agent.cellule?.toLowerCase().includes(term) ||
      agent.telephone?.toLowerCase().includes(term) ||
      agent.email?.toLowerCase().includes(term)
    );
  });
};

// Export
export const importCSV = async (csvData) => {
  const response = await api.post('/export/import-csv', { data: csvData });
  return response.data;
};

export const exportDatabase = async () => {
  const response = await api.post('/export/export-database');
  return response.data;
};

export const exportCSV = async () => {
  const response = await api.get('/export/csv', { responseType: 'blob' });
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `export_agents_${Date.now()}.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
  return response.data;
};

// Grades
export const getAllGrades = async () => {
  const response = await api.get('/grades');
  const data = response.data;
  if (data && data.success && Array.isArray(data.data)) {
    return data.data;
  }
  if (Array.isArray(data)) {
    return data;
  }
  return [];
};

export const getGradeById = async (id) => {
  const response = await api.get(`/grades/${id}`);
  return response.data?.data || response.data;
};

export const createGrade = async (gradeData) => {
  const response = await api.post('/grades', gradeData);
  return response.data;
};

export const updateGrade = async (id, gradeData) => {
  const response = await api.put(`/grades/${id}`, gradeData);
  return response.data;
};

export const deleteGrade = async (id) => {
  const response = await api.delete(`/grades/${id}`);
  return response.data;
};

// Fonctions
export const getAllFonctions = async () => {
  const response = await api.get('/fonctions');
  const data = response.data;
  if (data && data.success && Array.isArray(data.data)) {
    return data.data;
  }
  if (Array.isArray(data)) {
    return data;
  }
  return [];
};

export const getFonctionById = async (id) => {
  const response = await api.get(`/fonctions/${id}`);
  return response.data?.data || response.data;
};

export const createFonction = async (fonctionData) => {
  const response = await api.post('/fonctions', fonctionData);
  return response.data;
};

export const updateFonction = async (id, fonctionData) => {
  const response = await api.put(`/fonctions/${id}`, fonctionData);
  return response.data;
};

export const deleteFonction = async (id) => {
  const response = await api.delete(`/fonctions/${id}`);
  return response.data;
};

// Catégories Professionnelles
export const getAllCategoriesProfessionnelles = async () => {
  const response = await api.get('/categories-professionnelles');
  const data = response.data;
  if (data && data.success && Array.isArray(data.data)) {
    return data.data;
  }
  if (Array.isArray(data)) {
    return data;
  }
  return [];
};

export const getCategorieProfessionnelleById = async (id) => {
  const response = await api.get(`/categories-professionnelles/${id}`);
  return response.data?.data || response.data;
};

export const createCategorieProfessionnelle = async (categorieData) => {
  const response = await api.post('/categories-professionnelles', categorieData);
  return response.data;
};

export const updateCategorieProfessionnelle = async (id, categorieData) => {
  const response = await api.put(`/categories-professionnelles/${id}`, categorieData);
  return response.data;
};

export const deleteCategorieProfessionnelle = async (id) => {
  const response = await api.delete(`/categories-professionnelles/${id}`);
  return response.data;
};

export default api;

